const STORAGE_KEY = 'ocean_last_viewed_version'
const STORAGE_KEY_VIEWED_AT = 'ocean_version_viewed_at'
const STORAGE_KEY_VIEW_LOG = 'ocean_version_view_log'
const STORAGE_KEY_REFRESHED = 'ocean_last_refreshed_version'
// commit name for jira
interface VersionData {
  version: string
  date: string
  new_features?: string[]
  bug_fixes?: string[]
  planned_features?: string[]
}

interface VersionViewLog {
  version: string
  viewedAt: string
}


export const getLastViewedVersion = (): string | null => {
  return localStorage.getItem(STORAGE_KEY)
}


export const getLastViewedAt = (): Date | null => {
  const dateString = localStorage.getItem(STORAGE_KEY_VIEWED_AT)
  return dateString ? new Date(dateString) : null
}


export const markVersionAsViewed = (version: string): void => {
  const previousVersion = localStorage.getItem(STORAGE_KEY)
  
  localStorage.setItem(STORAGE_KEY, version)
  localStorage.setItem(STORAGE_KEY_VIEWED_AT, new Date().toISOString())
  
  const log = getVersionViewLog()
  log.push({
    version,
    viewedAt: new Date().toISOString(),
  })
  
  const trimmedLog = log.slice(-50)
  localStorage.setItem(STORAGE_KEY_VIEW_LOG, JSON.stringify(trimmedLog))
  
  if (import.meta.env.DEV && previousVersion !== version) {
    console.log(`[VersionTracking] User viewed version ${version} (previous: ${previousVersion})`)
  }
}


export const getVersionViewLog = (): VersionViewLog[] => {
  const logString = localStorage.getItem(STORAGE_KEY_VIEW_LOG)
  if (!logString) return []
  
  try {
    return JSON.parse(logString)
  } catch (e) {
    console.error('[VersionTracking] Failed to parse view log:', e)
    return []
  }
}


export const getUnreadVersionCount = (versions: VersionData[]): number => {
  if (!versions || versions.length === 0) return 0
  
  const lastViewed = getLastViewedVersion()
  if (!lastViewed) return versions.length 
  
  const lastViewedIndex = versions.findIndex(v => v.version === lastViewed)
  if (lastViewedIndex === -1) return versions.length 
  
  return lastViewedIndex
}


export const isVersionUnread = (versions: VersionData[], versionToCheck: string): boolean => {
  if (!versions || versions.length === 0) return false
  
  const lastViewed = getLastViewedVersion()
  if (!lastViewed) return true 
  
  const lastViewedIndex = versions.findIndex(v => v.version === lastViewed)
  const checkIndex = versions.findIndex(v => v.version === versionToCheck)
  
  return lastViewedIndex !== -1 && checkIndex < lastViewedIndex
}


export const hasUnreadVersions = (versions: VersionData[]): boolean => {
  return getUnreadVersionCount(versions) > 0
}

export const needsVersionRefresh = (latestVersion: string): boolean => {
  return localStorage.getItem(STORAGE_KEY_REFRESHED) !== latestVersion
}

export const markVersionRefreshed = (version: string): void => {
  localStorage.setItem(STORAGE_KEY_REFRESHED, version)
}


export const clearVersionTracking = (): void => {
  localStorage.removeItem(STORAGE_KEY)
  localStorage.removeItem(STORAGE_KEY_VIEWED_AT)
  localStorage.removeItem(STORAGE_KEY_VIEW_LOG)
  localStorage.removeItem(STORAGE_KEY_REFRESHED)
  if (import.meta.env.DEV) console.log('[VersionTracking] Cleared all version tracking data')
}

export const getVersionAnalytics = () => {
  const lastViewed = getLastViewedVersion()
  const lastViewedAt = getLastViewedAt()
  const viewLog = getVersionViewLog()
  
  return {
    lastViewedVersion: lastViewed,
    lastViewedAt: lastViewedAt?.toISOString() || null,
    totalVersionsViewed: new Set(viewLog.map(v => v.version)).size,
    viewHistory: viewLog,
  }
}

export default {
  getLastViewedVersion,
  getLastViewedAt,
  markVersionAsViewed,
  getVersionViewLog,
  getUnreadVersionCount,
  isVersionUnread,
  hasUnreadVersions,
  needsVersionRefresh,
  markVersionRefreshed,
  clearVersionTracking,
  getVersionAnalytics,
}
